import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  Alert
} from '@mui/material';
import { Close as CloseIcon, Phone as PhoneIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { useAgent } from '../../contexts/AgentContext';
import { useSoftphone } from '../../contexts/SoftphoneContext';
import { useApi } from '../../contexts/ApiContext';

/**
 * Valida un número de teléfono contra las reglas de números permitidos del skill
 * @param {string} phoneNumber - Número a validar
 * @param {object} allowedNumbersConfig - Configuración { default_action, rules: [{ condition, value, action }] }
 * @returns {object} { allowed: boolean, reason: string }
 */
const validateAllowedNumber = (phoneNumber, allowedNumbersConfig) => {
  console.log('[CLIENT VALIDATION] ========== INICIO VALIDACIÓN ==========');
  console.log('[CLIENT VALIDATION] Número a validar:', phoneNumber);
  console.log('[CLIENT VALIDATION] Config recibida:', JSON.stringify(allowedNumbersConfig, null, 2));

  // Si no hay configuración, permitir por defecto (para compatibilidad)
  if (!allowedNumbersConfig) {
    console.log('[CLIENT VALIDATION]  No hay configuración - PERMITIENDO por defecto');
    return { allowed: true, reason: 'Sin configuración de números permitidos' };
  }

  const { default_action = 'Accept', rules = [] } = allowedNumbersConfig;

  // Si no hay reglas, aplicar directamente la acción por defecto
  if (!rules || rules.length === 0) {
    console.log('[CLIENT VALIDATION]  No hay reglas configuradas');
    console.log(`[CLIENT VALIDATION] Aplicando default_action directamente: "${default_action}"`);

    if (default_action === 'Deny') {
      console.log('[CLIENT VALIDATION]  NÚMERO RECHAZADO por default_action (sin reglas)');
      return {
        allowed: false,
        reason: 'Número rechazado por acción por defecto (sin reglas configuradas)'
      };
    } else {
      console.log('[CLIENT VALIDATION]  NÚMERO PERMITIDO por default_action (sin reglas)');
      return {
        allowed: true,
        reason: 'Número permitido por acción por defecto (sin reglas configuradas)'
      };
    }
  }

  const phoneLength = phoneNumber.length;

  console.log('[CLIENT VALIDATION] Longitud del número:', phoneLength);
  console.log('[CLIENT VALIDATION] Total de reglas a evaluar:', rules.length);
  console.log('[CLIENT VALIDATION] Acción por defecto:', default_action);

  // Evaluar cada regla en orden
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    const { condition, value, action } = rule;
    let ruleMatches = false;

    console.log(`[CLIENT VALIDATION] --- Evaluando Regla ${i + 1}/${rules.length} ---`);
    console.log(`[CLIENT VALIDATION] Condición: "${condition}", Valor: "${value}", Acción: "${action}"`);

    switch (condition) {
      case 'Longitud <':
        ruleMatches = phoneLength < parseInt(value, 10);
        console.log(`[CLIENT VALIDATION] ${phoneLength} < ${value} = ${ruleMatches}`);
        break;
      case 'Longitud >':
        ruleMatches = phoneLength > parseInt(value, 10);
        console.log(`[CLIENT VALIDATION] ${phoneLength} > ${value} = ${ruleMatches}`);
        break;
      case 'Comienza con':
        ruleMatches = phoneNumber.startsWith(value);
        console.log(`[CLIENT VALIDATION] "${phoneNumber}".startsWith("${value}") = ${ruleMatches}`);
        break;
      case 'Termina con':
        ruleMatches = phoneNumber.endsWith(value);
        console.log(`[CLIENT VALIDATION] "${phoneNumber}".endsWith("${value}") = ${ruleMatches}`);
        break;
      case 'Contiene':
        ruleMatches = phoneNumber.includes(value);
        console.log(`[CLIENT VALIDATION] "${phoneNumber}".includes("${value}") = ${ruleMatches}`);
        break;
      default:
        console.warn(`[CLIENT VALIDATION] ❌ Condición DESCONOCIDA: "${condition}"`);
    }

    if (ruleMatches) {
      console.log(`[CLIENT VALIDATION]  REGLA COINCIDE - Aplicando acción: "${action}"`);
      if (action === 'Deny') {
        console.log(`[CLIENT VALIDATION]  NÚMERO RECHAZADO por regla ${i + 1}`);
        console.log('[CLIENT VALIDATION] ========== FIN VALIDACIÓN (DENY) ==========');
        return {
          allowed: false,
          reason: `Número rechazado por regla: ${condition} ${value}`
        };
      } else if (action === 'Accept') {
        console.log(`[CLIENT VALIDATION]  NÚMERO ACEPTADO por regla ${i + 1}`);
        console.log('[CLIENT VALIDATION] ========== FIN VALIDACIÓN (ACCEPT) ==========');
        return {
          allowed: true,
          reason: `Número aceptado por regla: ${condition} ${value}`
        };
      } else {
        console.warn(`[CLIENT VALIDATION] ⚠️ Acción DESCONOCIDA: "${action}" - ignorando regla`);
      }
    } else {
      console.log(`[CLIENT VALIDATION]  Regla NO coincide - continuando...`);
    }
  }

  // Si ninguna regla coincidió, aplicar acción por defecto
  console.log('[CLIENT VALIDATION]  NINGUNA REGLA COINCIDIÓ');
  console.log(`[CLIENT VALIDATION] Aplicando acción por defecto: "${default_action}"`);
  if (default_action === 'Deny') {
    console.log('[CLIENT VALIDATION]  NÚMERO RECHAZADO por default action');
    console.log('[CLIENT VALIDATION] ========== FIN VALIDACIÓN (DEFAULT DENY) ==========');
    return {
      allowed: false,
      reason: 'Número rechazado por acción por defecto'
    };
  }

  console.log('[CLIENT VALIDATION]  NÚMERO PERMITIDO por default action');
  console.log('[CLIENT VALIDATION] ========== FIN VALIDACIÓN (DEFAULT ACCEPT) ==========');
  return { allowed: true, reason: 'Número permitido por acción por defecto' };
};

const ClientDataPanel = ({ skillData, onClose, pendingTypification = false, agentStatusStartTime, agentStatus }) => {
  const { previewSession, currentAgent } = useAgent();
  const { makeCall, inCall, hasActiveRtp, setCallData, callData, setInCall, setStatus, holdCall: contextHoldCall, calls: contextCalls, currentCallId } = useSoftphone();
  const api = useApi();

  // Refs para manejar timeout de llamada
  const callTimeoutRef = useRef(null);
  const sipCallEstablishedRef = useRef(false);
  const currentManualCallId = useRef(null);
  const callingInProgressRef = useRef(false);
  const initialStartTimeRef = useRef(null);

  // Estado local para rastrear si es una llamada manual pendiente
  const [manualCallPending, setManualCallPending] = useState(false);

  // Detectar cuando se establece una llamada SIP real para cancelar timeout
  useEffect(() => {
    if (inCall && manualCallPending && !sipCallEstablishedRef.current) {
      // Validación estricta: verificar que la llamada establecida corresponde a nuestra llamada manual
      console.log('[ClientDataPanel]  inCall detectado, validando si es nuestra llamada manual...');
      console.log('[ClientDataPanel]  callData actual:', callData);
      console.log('[ClientDataPanel]  currentManualCallId:', currentManualCallId.current);

      // Solo cancelar timeout si:
      // 1. Hay callData válido
      // 2. Es de source 'preview' (nuestra llamada manual)
      // 3. Tiene el mismo ID que nuestra llamada manual
      // 4. Tiene actionId (confirmación del backend)
      // 5. NUEVO: hasActiveRtp es true (indica que hay transmisión RTP activa)
      const hasBackendConfirmation = callData && callData.actionId;
      const hasRealSipConnection = hasActiveRtp; // Usar RTP para verificar llamada real

      console.log('[ClientDataPanel]  Validando conexión RTP real:', {
        hasBackendConfirmation,
        hasRealSipConnection,
        inCallState: inCall,
        hasActiveRtp,
        needsBoth: hasBackendConfirmation && hasRealSipConnection
      });

      const isOurManualCall = callData &&
                              callData.source === 'preview' &&
                              callData.manualCallId === currentManualCallId.current &&
                              (hasBackendConfirmation && hasRealSipConnection); // AND: necesita AMBOS

      if (isOurManualCall) {
        console.log('[ClientDataPanel]  SIP call established CONFIRMADO para nuestra llamada manual - clearing timeout');
        sipCallEstablishedRef.current = true;
        setManualCallPending(false);
        currentManualCallId.current = null;
        initialStartTimeRef.current = null;

        // Limpiar timeout si existe
        if (callTimeoutRef.current) {
          clearTimeout(callTimeoutRef.current);
          callTimeoutRef.current = null;
        }
      } else {
        console.log('[ClientDataPanel]  Evento inCall detectado pero no corresponde a nuestra llamada manual - manteniendo timeout');
        console.log('[ClientDataPanel]  Validación fallida:', {
          hasCallData: !!callData,
          isPreview: callData?.source === 'preview',
          matchesId: callData?.manualCallId === currentManualCallId.current,
          hasBackendConfirmation,
          hasRealSipConnection,
          needsBoth: hasBackendConfirmation && hasRealSipConnection
        });
      }
    }
  }, [inCall, manualCallPending, callData]);

  // NOTA: Se removió la lógica automática de timeout para no interferir con llamadas válidas

  // Limpiar timeout al desmontar el componente
  useEffect(() => {
    return () => {
      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
      }
    };
  }, []);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');

  const hasSkillData = Boolean(skillData && skillData.skillData);
  const clientData = hasSkillData ? skillData.skillData : null;
  const configuredFieldNames = skillData?.datastructureFields || [];
  const phoneFields = skillData?.phoneFields || [];
  const actionId =
    callData?.actionId ||
    skillData?.actionId ||
    skillData?.action_id ||
    skillData?.skillData?.actionId ||
    skillData?.skillData?.action_id ||
    null;

  const handleCopyActionId = async () => {
    if (!actionId || typeof window === 'undefined') return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(actionId));
        return;
      }
    } catch (err) {
      console.warn('[ClientDataPanel] Clipboard write failed, falling back', err);
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = String(actionId);
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    } catch (err) {
      console.warn('[ClientDataPanel] Clipboard fallback failed', err);
    }
  };

  const fieldNames = React.useMemo(() => {
    if (configuredFieldNames.length > 0) {
      return configuredFieldNames;
    }

    const inferred = clientData && typeof clientData === 'object'
      ? Object.keys(clientData).filter((key) => !['customFields', 'phones', 'statuses'].includes(key))
      : [];

    const custom = clientData?.customFields && typeof clientData.customFields === 'object'
      ? Object.keys(clientData.customFields)
      : [];

    return Array.from(new Set([...inferred, ...custom]));
  }, [configuredFieldNames, clientData]);

  // Convert field names to field objects with title and name, adding phone info
  const fields = fieldNames.map(fieldName => {
    const phoneField = phoneFields.find(pf => pf.name === fieldName);
    return {
      name: fieldName,
      title: fieldName,
      isPhone: !!phoneField,
      numberMapping: phoneField?.number_mapping
    };
  });


  const handleCallPhone = async (phoneNumber, fieldName) => {
    // 🚨🚨🚨 LOG ULTRA PROMINENTE PARA DEBUG 🚨🚨🚨
    console.error('🔥🔥🔥 CLIENTDATAPANEL HANDLECALLPHONE EJECUTADO - VERSIÓN ACTUALIZADA 🔥🔥🔥');
    console.log('[ClientDataPanel] phoneNumber:', phoneNumber);
    console.log('[ClientDataPanel] fieldName:', fieldName);

    if (!phoneNumber || phoneNumber === 'N/A') {
      console.log('[ClientDataPanel] Invalid phone number:', phoneNumber);
      return;
    }

    // Guard sincrónico contra doble click (React state es async)
    if (callingInProgressRef.current) {
      console.log('[ClientDataPanel] Call already in progress (ref guard). Blocking duplicate.');
      return;
    }
    callingInProgressRef.current = true;

    try {
      // VALIDACIÓN: Máximo 10 llamadas simultáneas
      const activeCallCount = Object.keys(contextCalls || {}).length;
      if (activeCallCount >= 10) {
        console.log('[ClientDataPanel] Máximo 10 llamadas simultáneas. Bloqueando.');
        alert('Máximo 10 llamadas simultáneas. Finaliza una llamada antes de realizar otra.');
        return;
      }
      // Si hay una llamada activa, ponerla en hold
      if (inCall && currentCallId && contextHoldCall) {
        console.log('[ClientDataPanel] Poniendo llamada actual en hold:', currentCallId);
        contextHoldCall(currentCallId);
      }

      // En modo preview SIEMPRE usar el endpoint API para que use el contexto del skill
      // El makeCall directo usa el contexto "Local" del peer SIP
      console.log('[ClientDataPanel] Starting manual call - this should NOT close the panel');
      console.log('[ClientDataPanel] Using API endpoint for preview call with skill context');

      const skilldef = skillData?.skillConfig?.skilldef ||
                      previewSession?.skill ||
                      skillData?.skilldef ||
                      null;

      if (!skilldef) {
        console.error('[ClientDataPanel] skilldef not found');
        return;
      }

      // 🔍 VALIDACIÓN: Validar número contra reglas de números permitidos del skill
      console.log('[ClientDataPanel]  Iniciando validación de número permitido...');
      console.log('[ClientDataPanel] Skill a validar:', skilldef);
      console.log('[ClientDataPanel] Número a validar:', phoneNumber);

      const skillConfig = skillData?.skillConfig;
      console.log('[ClientDataPanel] skillData:', skillData);
      console.log('[ClientDataPanel] skillConfig:', skillConfig);
      console.log('[ClientDataPanel] skillConfig.allowed_numbers_config:', skillConfig?.allowed_numbers_config);

      if (skillConfig && skillConfig.allowed_numbers_config) {
        console.log('[ClientDataPanel]  Iniciando validación con reglas de números permitidos');

        const allowedConfig = typeof skillConfig.allowed_numbers_config === 'string'
          ? JSON.parse(skillConfig.allowed_numbers_config)
          : skillConfig.allowed_numbers_config;

        console.log('[ClientDataPanel] Config parseada:', allowedConfig);

        const validation = validateAllowedNumber(phoneNumber, allowedConfig);
        console.log('[ClientDataPanel] ========================================');
        console.log('[ClientDataPanel] RESULTADO FINAL DE VALIDACIÓN:', validation);
        console.log('[ClientDataPanel] ========================================');

        if (!validation.allowed) {
          console.log('[ClientDataPanel]  BLOQUEANDO LLAMADA - Número no permitido');
          alert(validation.reason || 'Número no permitido por las reglas del skill.');
          return;
        } else {
          console.log('[ClientDataPanel]  VALIDACIÓN EXITOSA - Número permitido');
        }
      } else {
        console.log('[ClientDataPanel]  No hay allowed_numbers_config - permitiendo por defecto');
      }

      // Obtener el dataId del previewSession o skillData
      const dataId = previewSession?.customerData?.id ||
                     skillData?.skillData?.id ||
                     null;

      console.log('[ClientDataPanel] Manual call with dataId:', dataId, 'skillDef:', skilldef);

      // 🚨 NUEVO: Establecer estado global inmediatamente al hacer click
      console.log('[ClientDataPanel]  ESTABLECIENDO ESTADO EN LLAMADA INMEDIATAMENTE!');
      setStatus("En llamada");
      setInCall(true);
      setManualCallPending(true);
      sipCallEstablishedRef.current = false;

      // Generar ID único para esta llamada manual
      const manualCallId = `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      currentManualCallId.current = manualCallId;
      console.log('[ClientDataPanel]  ID de llamada manual generado:', manualCallId);

      // Configurar callData inmediatamente para modo preview
      const initialStartTime = Date.now();
      initialStartTimeRef.current = initialStartTime;

      const initialCallData = {
        source: 'manual', // Marcar como manual para mostrar "Llamada saliente"
        dialingMode: 'Manual',
        dialingModeNormalized: 'manual',
        manualCallId: manualCallId, // ID único para identificar esta llamada
        phone: phoneNumber,
        skill: skilldef,
        data_id: dataId,
        startTime: initialStartTime,
        skillData: skillData // Preservar datos del skill para tipificación
      };
      setCallData(initialCallData);

      // Limpiar timeout anterior si existe
      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
        callTimeoutRef.current = null;
      }

      // Calcular tiempo en PRECALL si aplica
      let timePreviousReady = 0;
      if (agentStatus === 'PRECALL' && agentStatusStartTime) {
        timePreviousReady = Math.floor((Date.now() - agentStatusStartTime) / 1000);
        console.log(`[ClientDataPanel]  Tiempo en PRECALL calculado: ${timePreviousReady} segundos`);
      }

      const response = await fetch('/api/admin/dialer/manual', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('cti_admin_token')}`
        },
        body: JSON.stringify({
          phone: phoneNumber,
          skillDef: skilldef,
          dataId: dataId, // IMPORTANTE: Enviar el dataId para la tipificación
          // El backend automáticamente usa el contexto ${skilldef}-PREVIEW
          agent: currentAgent?.agent || currentAgent?.username || '',
          previewId: previewSession?.previewId || '',
          timePreviousReady: timePreviousReady // NUEVO: Tiempo en PRECALL en segundos
        })
      });

      const result = await response.json();

      // El backend puede responder success=true con actionId=null si el dedup bloquea la llamada.
      if (result?.deduplicated || !result?.actionId) {
        console.warn('[ClientDataPanel] Manual call blocked (dedup/no actionId). Resetting UI state.', result);
        alert(result?.message || 'Llamada manual bloqueada (duplicada). Intenta de nuevo en unos segundos.');
        setStatus("Conectado");
        setInCall(false);
        setCallData(null);
        setManualCallPending(false);
        currentManualCallId.current = null;
        initialStartTimeRef.current = null;
        if (callTimeoutRef.current) {
          clearTimeout(callTimeoutRef.current);
          callTimeoutRef.current = null;
        }
        return;
      }

      if (result.success) {
        console.log(`[ClientDataPanel] Call initiated to ${phoneNumber} (${fieldName})`);
        console.log(`[ClientDataPanel] ActionId received:`, result.actionId);

        // Actualizar callData con el actionId para la tipificación
        setCallData({
          ...initialCallData,
          actionId: result.actionId
        });
        console.log('[ClientDataPanel] Updated callData with actionId:', result.actionId);
      } else {
        console.error(`[ClientDataPanel] Error initiating call:`, result.message);
        // Si falla la llamada, revertir el estado y limpiar timeout
        setStatus("Conectado");
        setInCall(false);
        setCallData(null);
        setManualCallPending(false);
        currentManualCallId.current = null;
        initialStartTimeRef.current = null;

        if (callTimeoutRef.current) {
          clearTimeout(callTimeoutRef.current);
          callTimeoutRef.current = null;
        }
      }
    } catch (error) {
      console.error('[ClientDataPanel] Error making manual call:', error);
      // Si hay error, revertir el estado y limpiar timeout
      setStatus("Conectado");
      setInCall(false);
      setCallData(null);
      setManualCallPending(false);
      currentManualCallId.current = null;
      initialStartTimeRef.current = null;

      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
        callTimeoutRef.current = null;
      }
    } finally {
      callingInProgressRef.current = false;
    }
  };

  const refreshClientData = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    setRefreshError('');
    try {
      const actionId =
        callData?.actionId ||
        skillData?.actionId ||
        skillData?.action_id ||
        skillData?.skillData?.actionId ||
        skillData?.skillData?.action_id ||
        null;
      const skill = callData?.skill || skillData?.skillConfig?.skilldef || skillData?.skilldef || null;
      const dataId = callData?.data_id || callData?.dataId || skillData?.skillData?.id || null;

      if (skill && dataId) {
        const fetched = await api.softphone.getSkillData(skill, dataId);
        const updated = {
          ...(callData || {}),
          actionId: callData?.actionId || actionId,
          skill,
          data_id: dataId,
          callNumber: callData?.callNumber || callData?.phone_number,
          phone_number: callData?.phone_number || callData?.callNumber,
          skillData: fetched || null,
          source: callData?.source || 'predictive',
          timestamp: new Date().toISOString()
        };
        setCallData(updated);
      } else if (actionId) {
        const meta = await api.softphone.getCallMetadata(actionId);
        if (meta) {
          const updated = {
            actionId: meta.actionId || actionId,
            skill: meta.skill,
            data_id: meta.data_id,
            callNumber: meta.phone_number,
            phone_number: meta.phone_number,
            idlog_calls: meta.idlog_calls,
            idload: meta.idload,
            skillData: meta.skillData || null,
            source: 'predictive',
            timestamp: new Date().toISOString()
          };
          setCallData(updated);
        }
      } else {
        setRefreshError('No hay identificadores para buscar datos.');
      }
    } catch (err) {
      setRefreshError(err?.message || 'Error al refrescar datos.');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        width: 320,
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #e0e0e0'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f5f5f5'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Datos del Cliente
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: '#e0e0e0'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {pendingTypification && (
          <Alert severity="warning" sx={{ m: 2 }}>
            Debe tipificar la llamada anterior antes de realizar una nueva llamada
          </Alert>
        )}
        {!hasSkillData && (
          <Box sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Datos del cliente no disponibles. Puedes intentar recargar.
            </Alert>
            {refreshError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {refreshError}
              </Alert>
            )}
            <Button
              variant="contained"
              onClick={refreshClientData}
              disabled={isRefreshing}
              sx={{ width: '100%' }}
            >
              {isRefreshing ? 'Cargando...' : 'Refrescar datos'}
            </Button>
          </Box>
        )}
        <List dense>
          {actionId && (
            <>
              <ListItem>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                        ActionID
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleCopyActionId}
                        title="Copiar ActionID"
                        aria-label="Copiar ActionID"
                        sx={{
                          color: '#667eea',
                          '&:hover': { backgroundColor: '#e3f2fd' }
                        }}
                      >
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  }
                  secondary={
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                      {actionId}
                    </Typography>
                  }
                />
              </ListItem>
              {fields.length > 0 && <Divider />}
            </>
          )}
          {fields.map((field, index) => {
            const rawValue = clientData[field.name] ?? clientData?.customFields?.[field.name];
            const value =
              rawValue === null || rawValue === undefined || rawValue === ''
                ? 'N/A'
                : (typeof rawValue === 'object' ? JSON.stringify(rawValue) : rawValue);
            const isPhoneField = field.isPhone;

            return (
              <React.Fragment key={field.name}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                          {field.title || field.name}
                          {isPhoneField && (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: '#667eea', fontWeight: 400 }}>
                              ({field.numberMapping})
                            </Typography>
                          )}
                        </Typography>
                        {isPhoneField && value && value !== 'N/A' && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PhoneIcon />}
                            onClick={(e) => { e.stopPropagation(); handleCallPhone(value, field.name); }}
                            disabled={pendingTypification || inCall}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              py: 0.5,
                              fontSize: '0.75rem',
                              color: pendingTypification ? '#999' : (inCall ? '#4caf50' : '#667eea'),
                              borderColor: pendingTypification ? '#ccc' : (inCall ? '#4caf50' : '#667eea'),
                              '&:hover': {
                                backgroundColor: pendingTypification ? 'transparent' : (inCall ? '#e8f5e8' : '#e3f2fd'),
                                borderColor: pendingTypification ? '#ccc' : (inCall ? '#4caf50' : '#1565c0')
                              }
                            }}
                            title={pendingTypification ? 'Debe tipificar la llamada anterior antes de continuar' : (inCall ? 'Llamada en curso' : '')}
                          >
                            {pendingTypification ? 'Tipificar primero' : (inCall ? 'En llamada' : 'Llamar')}
                          </Button>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                        {value}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < fields.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Paper>
  );
};

export default ClientDataPanel;
